import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  folder?: string;
  accept?: string;
  maxSizeMB?: number;
  /** When true, the file is uploaded to the public bucket and a permanent URL is returned. */
  public?: boolean;
  onUploaded: (result: { fileUrl: string; fileKey: string; filename: string }) => void;
  onRemoved?: () => void;
  className?: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(',')[1];
      if (base64) {
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function FileUpload({
  folder = 'uploads',
  accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx',
  maxSizeMB = 10,
  public: isPublic = false,
  onUploaded,
  onRemoved,
  className,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{ filename: string; fileKey: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const uploadMutation = trpc.upload.uploadFile.useMutation();

  const handleUpload = useCallback(async (file: File) => {
    setError(null);

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size exceeds ${maxSizeMB}MB limit`);
      return;
    }

    setUploading(true);
    try {
      const base64Data = await fileToBase64(file);

      const { key, url } = await uploadMutation.mutateAsync({
        filename: file.name,
        folder,
        contentType: file.type || 'application/octet-stream',
        base64Data,
        public: isPublic || undefined,
      });

      setUploadedFile({ filename: file.name, fileKey: key });
      onUploaded({ fileUrl: url || '', fileKey: key, filename: file.name });
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [folder, maxSizeMB, isPublic, uploadMutation, onUploaded]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setError(null);
    onRemoved?.();
  };

  if (uploadedFile) {
    return (
      <div className={cn('flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2', className)}>
        <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm truncate flex-1">{uploadedFile.filename}</span>
        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleRemove}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-4 cursor-pointer transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
          uploading && 'pointer-events-none opacity-60'
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
        {uploading ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="mt-1 text-xs text-muted-foreground">Uploading...</p>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="mt-1 text-xs text-muted-foreground">
              Click or drag file to upload
            </p>
            <p className="text-[10px] text-muted-foreground/70">
              Max {maxSizeMB}MB
            </p>
          </>
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
